import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ShareModal = ({ document, onClose }) => {
  const [shareWith, setShareWith] = useState('');
  const [permissions, setPermissions] = useState('view');
  const [message, setMessage] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [requirePassword, setRequirePassword] = useState(false);
  const [password, setPassword] = useState('');

  const shareOptions = [
    { value: 'client', label: 'Client Portal', icon: 'Users' },
    { value: 'email', label: 'Email Address', icon: 'Mail' },
    { value: 'link', label: 'Shareable Link', icon: 'Link' },
    { value: 'opposing', label: 'Opposing Counsel', icon: 'Scale' }
  ];

  const permissionLevels = [
    { value: 'view', label: 'View Only', description: 'Can view and download' },
    { value: 'comment', label: 'Comment', description: 'Can view, download, and add comments' },
    { value: 'edit', label: 'Edit', description: 'Can view, download, comment, and edit' }
  ];

  const currentShares = [
    {
      id: 1,
      name: 'James Mwangi',
      email: 'james.mwangi@email.com',
      type: 'Client',
      permission: 'View',
      sharedDate: '2024-01-15',
      lastAccessed: '2024-01-15'
    },
    {
      id: 2,
      name: 'Opposing Counsel',
      email: 'counsel@lawfirm.co.ke',
      type: 'External',
      permission: 'View',
      sharedDate: '2024-01-14',
      lastAccessed: 'Never'
    }
  ];

  const handleShare = () => {
    console.log('Sharing document:', {
      documentId: document.id,
      shareWith,
      permissions,
      message,
      expiryDate,
      requirePassword,
      password
    });
    onClose();
  };

  const revokeAccess = (shareId) => {
    console.log('Revoking access for share:', shareId);
  };

  const copyLink = () => {
    const link = `https://lawdesk.ke/shared/${document.id}`;
    navigator.clipboard.writeText(link);
    // Show toast notification
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-dialog w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-heading font-semibold text-text-primary">Share Document</h2>
            <p className="text-sm text-text-secondary mt-1">{document.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Share Options */}
          <div className="mb-6">
            <h3 className="font-medium text-text-primary mb-4">Share With</h3>
            <div className="grid grid-cols-2 gap-3">
              {shareOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setShareWith(option.value)}
                  className={`flex items-center p-3 border rounded-lg transition-smooth ${
                    shareWith === option.value
                      ? 'border-primary bg-primary-50 text-primary' :'border-border hover:border-primary hover:bg-primary-50'
                  }`}
                >
                  <Icon name={option.icon} size={20} className="mr-3" />
                  <span className="font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Email Input for Email Share */}
          {shareWith === 'email' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}

          {/* Permissions */}
          <div className="mb-6">
            <h3 className="font-medium text-text-primary mb-4">Permission Level</h3>
            <div className="space-y-3">
              {permissionLevels.map(level => (
                <label key={level.value} className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="permissions"
                    value={level.value}
                    checked={permissions === level.value}
                    onChange={(e) => setPermissions(e.target.value)}
                    className="mt-1 mr-3 text-primary focus:ring-primary"
                  />
                  <div>
                    <div className="font-medium text-text-primary">{level.label}</div>
                    <div className="text-sm text-text-secondary">{level.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Options */}
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Message (Optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message for the recipient..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Expiry Date (Optional)
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={requirePassword}
                    onChange={(e) => setRequirePassword(e.target.checked)}
                    className="mr-2 text-primary focus:ring-primary"
                  />
                  <span className="text-sm font-medium text-text-primary">Require Password</span>
                </label>
              </div>
            </div>

            {requirePassword && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            )}
          </div>

          {/* Current Shares */}
          <div className="mb-6">
            <h3 className="font-medium text-text-primary mb-4">Currently Shared With</h3>
            <div className="space-y-3">
              {currentShares.map(share => (
                <div key={share.id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <div>
                      <div className="font-medium text-text-primary">{share.name}</div>
                      <div className="text-sm text-text-secondary">{share.email} • {share.type}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-text-secondary">{share.permission}</span>
                    <button
                      onClick={() => revokeAccess(share.id)}
                      className="text-error hover:bg-error-50 p-1 rounded transition-smooth"
                      title="Revoke Access"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="border-t border-border pt-6">
            <h3 className="font-medium text-text-primary mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={copyLink}
                className="flex items-center px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-smooth"
              >
                <Icon name="Link" size={16} className="mr-2" />
                Copy Link
              </button>
              <button className="flex items-center px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-smooth">
                <Icon name="Download" size={16} className="mr-2" />
                Download
              </button>
              <button className="flex items-center px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-smooth">
                <Icon name="Mail" size={16} className="mr-2" />
                Email Link
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-text-secondary hover:text-text-primary transition-smooth"
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={!shareWith}
            className="bg-primary text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Share Document
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;